import _ from 'lodash';

// Helpers de cupom de desconto compartilhados entre a listagem (Lista.vue)
// e o modal de cadastro/edição (DialogCupomForm.vue).

// Gera um código de cupom a partir do nome do estabelecimento e do valor do desconto.
export function gerarCodigoCupom (cupom, nomeEstabelecimento) {
  const nome = (nomeEstabelecimento || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const sufixo = chars.charAt(Math.floor(Math.random() * chars.length))
               + chars.charAt(Math.floor(Math.random() * chars.length));
  let base;
  if (cupom.entrega_gratis) {
    base = nome.substring(0, 4) + 'FRT';
  } else {
    const valor = String(cupom.valor_desconto);
    const nomeChars = Math.max(2, 7 - valor.length);
    base = nome.substring(0, nomeChars) + valor;
  }
  if (base.length < 5) base = base.padEnd(5, '0');
  return base.substring(0, 5) + sufixo;
}

// Detecta qual preset de vigência corresponde ao intervalo de datas informado.
// Retorna '14d' | '28d' | '3m' | 'personalizado' | null.
export function detectarPresetVigencia (dataInicio, dataFim) {
  if (!dataInicio || !dataFim) return null;
  const dayjs = window.getApp.$dayjs;
  const di = dayjs(dataInicio);
  const df = dayjs(dataFim);
  if (!di.isValid() || !df.isValid()) return null;
  const diffDays = df.diff(di, 'day');
  if (diffDays === 14) return '14d';
  if (diffDays === 28) return '28d';
  if (diffDays >= 89 && diffDays <= 92) return '3m';
  return 'personalizado';
}

// Regra canônica de "cupom ativo", espelhando o mobile (CupomTemplateService.isAtivo):
// flag ativo ligada, com saldo disponível (quantidade - utilizados > 0) e não expirado.
// Fonte da verdade para a contagem de cupons ativos e o nível de destaque.
export function isCupomAtivo (cupom) {
  const dayjs = window.getApp.$dayjs;
  const disponiveis = (cupom.quantidade || 0) - (cupom.utilizados || 0);
  return cupom.ativo === 1 &&
    disponiveis > 0 &&
    (!cupom.data_fim || !dayjs(cupom.data_fim).isBefore(dayjs(), 'day'));
}

// Verifica se um template sugerido já possui cupom equivalente cadastrado
// (ativo, vigente e com saldo). Extraído de Lista.vue para reuso na tela de
// Marketing (prévia de cupons não ativados).
export function isTemplateJaCadastrado (suggested, cupons) {
  const suggestedEntrega = !!suggested.entrega_gratis || Number(suggested.taxa_entrega) === 1;
  return !!(cupons || []).find(cupom => {
    const cupomEntrega = Number(cupom.taxa_entrega) === 1 || Number(cupom.entrega_gratis) === 1;
    const e = suggested.tipo_desconto === cupom.tipo_desconto;
    let f;
    if (suggested.tipo_desconto === 2) {
      f = suggested.valor_desconto / 100 === cupom.valor_desconto;
    } else {
      f = suggested.valor_desconto === cupom.valor_desconto;
    }
    const g = suggested.valor_minimo === cupom.valor_minimo;
    // Cupons de entrega grátis: o valor máximo de desconto é escolhido na
    // ativação, então não deve diferenciar o cupom do template correspondente.
    const k = (suggestedEntrega && cupomEntrega)
      ? true
      : suggested.valor_desconto_maximo === cupom.valor_desconto_maximo;
    const l = (suggested.primeiro_pedido_estabelecimento || 0) === (cupom.primeiro_pedido_estabelecimento || 0);
    const d = (suggested.destaque || 0) === (cupom.destaque || 0);
    return e && f && g && k && l && d &&
      suggestedEntrega === cupomEntrega &&
      cupom.ativo === 1 && cupom.status === 1 && cupom.disponiveis > 0;
  });
}

// Filtra as seções de templates removendo os que já têm cupom equivalente
// cadastrado; descarta seções vazias.
export function filtrarSectionsPendentes (sections, cupons) {
  return (sections || [])
    .map(sec => Object.assign({}, sec, {
      templates: (sec.templates || []).filter(t => !isTemplateJaCadastrado(t, cupons)),
    }))
    .filter(sec => sec.templates.length > 0);
}

// Monta o payload final do cupom para envio à API.
// ctx: { fromTemplate, estabelecimento, isCardapioDigital, aplicarEm,
//        minPedidoEntregaSelecionado, regraDescontoEntrega,
//        valorDescontoMaximoEntrega, diasSemanaSel }
export function montarPayloadCupom (cupom, ctx) {
  ctx = ctx || {};
  const dayjs = window.getApp.$dayjs;
  const obj = _.cloneDeep(cupom);
  const fromTemplate = !!ctx.fromTemplate;

  // Código sempre gravado em maiúsculas (o input não usa mais máscara).
  obj.codigo = (obj.codigo || '').toString().trim().toUpperCase();

  if (!fromTemplate && ctx.aplicarEm === 'entrega') {
    obj.tipo_desconto = 2;
    obj.valor_desconto = 1;
    obj.valor_minimo = ctx.minPedidoEntregaSelecionado || 0;
    obj.valor_desconto_maximo = ctx.regraDescontoEntrega === 'limitado' ? ctx.valorDescontoMaximoEntrega : null;
    obj.taxa_entrega = 1;
  } else if (!fromTemplate) {
    obj.taxa_entrega = 0;
  }

  obj.data_inicio = obj.vigencia == 1 && obj.data_inicio ? dayjs(obj.data_inicio, 'YYYY-MM-DD').format('YYYY-MM-DD 00:00:00') : 'NULL';
  obj.data_fim = obj.vigencia == 1 && obj.data_fim ? dayjs(obj.data_fim, 'YYYY-MM-DD').format('YYYY-MM-DD 23:59:59') : 'NULL';

  const isEntregaManual = !fromTemplate && ctx.aplicarEm === 'entrega';
  if (!obj.id && obj.tipo_desconto == 2 && !isEntregaManual) obj.valor_desconto = (obj.valor_desconto / 100);

  obj.estabelecimento_id = ctx.estabelecimento ? ctx.estabelecimento.id : obj.estabelecimento_id;
  obj.localidades = ctx.estabelecimento ? ctx.estabelecimento.localidade_id : obj.localidades;
  obj.tipo_desconto_maximo = obj.tipo_desconto == 1 ? 1 : 2;
  obj.ativo = obj.id ? (obj.ativo != null ? obj.ativo : 1) : 1;
  obj.tipo_quantidade_total = obj.tipo_quantidade_total || 0;
  obj.tipo_quantidade_usuario = obj.tipo_quantidade_usuario || 0;
  obj.destaque = ctx.isCardapioDigital ? 0 : (obj.destaque || 0);
  obj.dias_semana = fromTemplate
    ? (obj.dias_semana || '0,1,2,3,4,5,6')
    : (ctx.diasSemanaSel || []).slice().sort((a, b) => a - b).join(',');

  delete obj.title;
  delete obj.category;
  delete obj.tagline;
  delete obj.inteligente;
  delete obj.entrega_gratis;
  delete obj.dialog;
  return obj;
}
